import { useState } from "react";
import PropTypes from 'prop-types';
import { Menu, X } from "lucide-react";
import { Link, NavLink as RouterNavLink } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faInstagram } from '@fortawesome/free-brands-svg-icons';

const SOCIAL_LINKS = [
  { href: "https://web.facebook.com/colegionmva", icon: faFacebookF, label: "Visitar nuestra página de Facebook" },
  { href: "https://www.instagram.com/colegionmva", icon: faInstagram, label: "Visitar nuestra página de Instagram" },
];

// Enlaces del nivel superior (a la derecha del logo en escritorio)
const SECONDARY_LINKS = [
  { to: "/quienes-somos", text: "Quiénes somos" },
  { to: "/admision", text: "Admisión 2027" },
];

export default function Navbar({ links = defaultLinks() }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <>
      {/* Móvil: barra verde compacta con menú desplegable */}
      <header className="md:hidden w-full px-4 h-16 flex items-center bg-green-900 justify-between shadow-lg sticky top-0 z-50" role="banner">
        <Link className="flex items-center" to="/" aria-label="Ir al inicio">
          <img
            src="/01-images/logo.webp"
            alt="Logo Colegio Nuevo Milenio - Educación de calidad en Villa Alemana"
            width={100}
            height={100}
            loading="eager"
            className="mr-4"
          />
        </Link>
        <button
          className="text-beige-100 hover:text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 rounded"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-menu"
          aria-label={isMobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
        >
          {isMobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
        </button>
        {isMobileMenuOpen && (
          <MobileMenu links={links} onClose={() => setIsMobileMenuOpen(false)} />
        )}
      </header>

      {/* Escritorio, nivel superior: logo y enlaces secundarios sobre fondo blanco */}
      <header className="hidden md:block w-full bg-white" role="banner">
        <div className="container mx-auto px-6 lg:px-8 h-[4.5rem] flex items-center justify-between">
          <Link to="/" aria-label="Colegio Nuevo Milenio - Ir al inicio" className="block">
            {/* El logo es blanco con fondo transparente: se usa como máscara para teñirlo de verde */}
            <div
              role="img"
              aria-label="Logo Colegio Nuevo Milenio - Educación de calidad en Villa Alemana"
              className="h-12 w-[139px] bg-green-900"
              style={{
                WebkitMaskImage: "url(/01-images/logo.webp)",
                maskImage: "url(/01-images/logo.webp)",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskSize: "contain",
                maskSize: "contain",
                WebkitMaskPosition: "left center",
                maskPosition: "left center",
              }}
            />
          </Link>

          <div className="flex items-center gap-6 text-green-900">
            <nav className="flex items-center gap-6" aria-label="Enlaces secundarios">
              {SECONDARY_LINKS.map((link) => (
                link.to ? (
                  <Link key={link.text} to={link.to} className={secondaryLinkClass}>
                    {link.text}
                  </Link>
                ) : (
                  <a key={link.text} href={link.href} target="_blank" rel="noopener noreferrer" className={secondaryLinkClass}>
                    {link.text}
                  </a>
                )
              ))}
            </nav>
            <div className="flex items-center gap-4 pl-6 border-l border-green-900/20">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.href}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-green-700 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-900 rounded"
                  aria-label={social.label}
                >
                  <FontAwesomeIcon icon={social.icon} size="lg" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Escritorio, nivel principal: barra verde que queda fija al hacer scroll */}
      <nav className="hidden md:block w-full bg-green-900 shadow-md sticky top-0 z-50" role="navigation" aria-label="Navegación principal">
        <div className="container mx-auto px-6 lg:px-8 h-11 flex items-center justify-center gap-8 lg:gap-12">
          {links.map((link) => (
            <MainLink key={link.to} to={link.to} text={link.text} />
          ))}
        </div>
      </nav>
    </>
  );
}

Navbar.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      to: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    })
  ),
};

const secondaryLinkClass =
  "text-sm font-medium hover:underline underline-offset-4 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-900 rounded";

function defaultLinks() {
  return [
    { to: "/", text: "Inicio" },
    { to: "/becas", text: "Becas 2027" },
    { to: "/documentos", text: "Documentos" },
    { to: "/lista-de-utiles", text: "Lista de útiles" },
    // { to: "/galeria", text: "Galería" },
  ];
}

// Enlace de la barra principal (escritorio): mayúsculas, espaciado y subrayado en la página activa
function MainLink({ to, text }) {
  return (
    <RouterNavLink
      to={to}
      end={to === "/"}
      className={({ isActive }) =>
        `h-full flex items-center gap-2 border-b-[3px] text-sm font-medium uppercase tracking-widest text-white transition-colors duration-200 focus:outline-none focus-visible:bg-white/10 ${
          isActive ? "border-beige-100" : "border-transparent hover:border-white/50"
        }`
      }
    >
      {text}
      {text === "Lista de útiles" && <ListaUtilesBadge />}
    </RouterNavLink>
  );
}

MainLink.propTypes = {
  to: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
};

function ListaUtilesBadge() {
  return (
    <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse shadow-md tracking-normal">
      2026
    </span>
  );
}

function NavLink({ to, text }) {
  const isListaUtiles = text === "Lista de útiles";

  return (
    <Link
      className="text-base font-medium text-beige-100 hover:text-white hover:underline underline-offset-4 transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 rounded flex items-center relative"
      to={to}
      aria-label={`Ir a ${text}`}
    >
      {text}
      {isListaUtiles && <span className="ml-2"><ListaUtilesBadge /></span>}
    </Link>
  );
}

NavLink.propTypes = {
  to: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
};

function MobileMenu({ links, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" id="mobile-menu">
      <div className="bg-green-900 text-white w-64 max-w-full p-6 shadow-lg transform transition-transform duration-300 ease-in-out translate-x-0">
        <nav className="flex flex-col mt-8 space-y-4" role="navigation" aria-label="Menú móvil">
          {links.length > 0 ? (
            links.map((link) => (
              <NavLink key={link.to} to={link.to} text={link.text} />
            ))
          ) : (
            <p>Cargando...</p>
          )}
        </nav>
        <nav className="flex flex-col mt-6 pt-6 space-y-4 border-t border-white/20" aria-label="Enlaces secundarios">
          {SECONDARY_LINKS.map((link) => (
            link.to ? (
              <NavLink key={link.text} to={link.to} text={link.text} />
            ) : (
              <a
                key={link.text}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-base font-medium text-beige-100 hover:text-white hover:underline underline-offset-4 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 rounded"
              >
                {link.text}
              </a>
            )
          ))}
        </nav>
        <div className="flex justify-center mt-8 space-x-4">
          {SOCIAL_LINKS.map((social) => (
            <a
              key={social.href}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 rounded"
              aria-label={social.label}
            >
              <FontAwesomeIcon icon={social.icon} size="lg" aria-hidden="true" />
            </a>
          ))}
        </div>
      </div>
      <div className="flex-1 bg-black bg-opacity-50" onClick={onClose}></div>
    </div>
  );
}

MobileMenu.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      to: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
};
